import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  ProductAddon,
  ProductAddonCreateRequest,
  ProductAddonType,
  ProductAddonUpdateRequest,
} from "../../types/order";

const productAddonSchema = z.object({
  index_key: z.string().min(1, "Index key is required"),
  type: z.enum(["VALUE_ADD", "INSTRUCTION"]),
  custom_product: z.string().min(1, "Custom product is required"),
  custom_product_search: z.string().min(1, "Custom product search is required"),
});

type FormValues = z.infer<typeof productAddonSchema>;

type PropsType = {
  addon?: ProductAddon | null;
  onSubmit: (
    ...args: [ProductAddonCreateRequest | ProductAddonUpdateRequest]
  ) => Promise<void>;
  onCancel: () => void;
};

const typeOptions: { label: string; value: ProductAddonType }[] = [
  { label: "Value Add", value: "VALUE_ADD" },
  { label: "Instruction", value: "INSTRUCTION" },
];

const ProductAddonForm = ({ addon, onSubmit, onCancel }: PropsType) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!addon;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(productAddonSchema),
    defaultValues: {
      index_key: addon?.index_key || "",
      type: addon?.type || "VALUE_ADD",
      custom_product: addon?.custom_product || "",
      custom_product_search: addon?.custom_product_search || "",
    },
  });

  const handleFormSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data as ProductAddonCreateRequest);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Controller
            name="index_key"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Index Key"
                required
                fullWidth
                error={!!errors.index_key}
                helperText={errors.index_key?.message}
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Type"
                required
                fullWidth
                error={!!errors.type}
                helperText={errors.type?.message}
                disabled={isSubmitting}
              >
                {typeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>

        <Controller
          name="custom_product"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Custom Product"
              required
              fullWidth
              multiline
              rows={2}
              error={!!errors.custom_product}
              helperText={errors.custom_product?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <Controller
          name="custom_product_search"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Custom Product Search"
              required
              fullWidth
              error={!!errors.custom_product_search}
              helperText={errors.custom_product_search?.message}
              disabled={isSubmitting}
            />
          )}
        />

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ pt: 2 }}
        >
          <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? "Update Add-on" : "Add Add-on"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProductAddonForm;
